import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Card, CardContent, ConfirmDialog, Input, RichTextEditor } from '@mlm/ui';
import {
  createAnnouncement,
  deleteAnnouncement,
  listAnnouncements,
  publishAnnouncement,
  updateAnnouncement,
  type CourseAnnouncement,
} from '../lib/api';

/**
 * Course announcements tab — the creator's broadcast channel to the students
 * enrolled in THIS course.
 *
 * Publishing is a one-way door: it rings every live enrollee's bell, exactly
 * once, and can never be re-fired. That is why it is a separate confirmed
 * action rather than a checkbox on save, and why editing a published
 * announcement is allowed but silent (an edit is a correction, not news).
 */
export function CourseAnnouncementsTab({ courseId }: { courseId: string }) {
  const qc = useQueryClient();
  const [composing, setComposing] = useState(false);
  const [editing, setEditing] = useState<CourseAnnouncement | null>(null);
  const [confirmPublish, setConfirmPublish] = useState<CourseAnnouncement | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<CourseAnnouncement | null>(null);

  const listQ = useQuery({
    queryKey: ['creator-announcements', courseId],
    queryFn: () => listAnnouncements(courseId),
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['creator-announcements', courseId] });

  const publish = useMutation({
    mutationFn: (id: string) => publishAnnouncement(id),
    onSuccess: (res) => {
      invalidate();
      setConfirmPublish(null);
      // The count is the point of the confirmation — say what actually happened.
      qc.setQueryData(['creator-announcements-last-notified'], res.notified);
    },
  });
  const remove = useMutation({
    meta: { successMessage: 'Announcement deleted.' },
    mutationFn: (id: string) => deleteAnnouncement(id),
    onSuccess: () => {
      invalidate();
      setConfirmDelete(null);
    },
  });

  const items = listQ.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-semibold">Announcements</h2>
          <p className="text-sm text-muted-foreground">
            Posted to the students enrolled in this course. Publishing notifies them once.
          </p>
        </div>
        <Button onClick={() => setComposing(true)}>New announcement</Button>
      </div>

      {listQ.isLoading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : listQ.isError ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            Could not load announcements.{' '}
            <button
              type="button"
              className="text-primary hover:underline"
              onClick={() => listQ.refetch()}
            >
              Try again
            </button>
          </CardContent>
        </Card>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            No announcements yet.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((a) => (
            <Card key={a.id}>
              <CardContent className="space-y-2 py-4">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="truncate font-medium">{a.title}</h3>
                      <Badge variant={a.publishedAt ? 'success' : 'secondary'}>
                        {a.publishedAt ? 'Published' : 'Draft'}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {a.publishedAt
                        ? `Published ${new Date(a.publishedAt).toLocaleString()}`
                        : `Created ${new Date(a.createdAt).toLocaleString()}`}
                    </p>
                  </div>
                  <div className="flex flex-none gap-3">
                    <button
                      type="button"
                      className="text-xs text-primary hover:underline"
                      onClick={() => setEditing(a)}
                    >
                      Edit
                    </button>
                    {!a.publishedAt && (
                      <button
                        type="button"
                        className="text-xs text-primary hover:underline"
                        onClick={() => setConfirmPublish(a)}
                      >
                        Publish
                      </button>
                    )}
                    <button
                      type="button"
                      className="text-xs text-destructive hover:underline"
                      onClick={() => setConfirmDelete(a)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
                <div
                  className="prose prose-sm max-w-none text-sm text-muted-foreground"
                  // Sanitized server-side by the same pipeline as article lessons.
                  dangerouslySetInnerHTML={{ __html: a.body }}
                />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {composing && <AnnouncementDialog courseId={courseId} onClose={() => setComposing(false)} />}
      {editing && (
        <AnnouncementDialog
          key={editing.id}
          courseId={courseId}
          announcement={editing}
          onClose={() => setEditing(null)}
        />
      )}

      <ConfirmDialog
        open={!!confirmPublish}
        onOpenChange={(o) => !o && setConfirmPublish(null)}
        title="Publish this announcement?"
        description="Every student currently enrolled in this course gets a notification. This happens once — editing it afterwards will not notify anyone again."
        confirmLabel="Publish and notify"
        loading={publish.isPending}
        onConfirm={() => {
          if (confirmPublish) publish.mutate(confirmPublish.id);
        }}
      />
      <ConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
        title="Delete this announcement?"
        description="Students keep any notification already delivered — a delivered message is not un-sent by deleting the post."
        confirmLabel="Delete"
        confirmVariant="destructive"
        loading={remove.isPending}
        onConfirm={() => {
          if (confirmDelete) remove.mutate(confirmDelete.id);
        }}
      />
    </div>
  );
}

function AnnouncementDialog({
  courseId,
  announcement,
  onClose,
}: {
  courseId: string;
  announcement?: CourseAnnouncement;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const isEdit = !!announcement;
  const [title, setTitle] = useState(announcement?.title ?? '');
  const [body, setBody] = useState(announcement?.body ?? '');
  const [publishNow, setPublishNow] = useState(false);

  const save = useMutation({
    meta: { successMessage: 'Announcement saved.' },
    mutationFn: () =>
      isEdit
        ? updateAnnouncement(announcement!.id, { title: title.trim(), body })
        : createAnnouncement(courseId, { title: title.trim(), body, publish: publishNow }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['creator-announcements', courseId] });
      onClose();
    },
  });

  const valid = title.trim().length > 0 && body.replace(/<[^>]*>/g, '').trim().length > 0;

  return (
    <ConfirmDialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={isEdit ? 'Edit announcement' : 'New announcement'}
      description={
        isEdit
          ? 'Editing a published announcement updates what students read. It does not notify them again.'
          : 'Save it as a draft, or publish it now to notify every enrolled student.'
      }
      confirmLabel={
        save.isPending ? 'Saving…' : isEdit ? 'Save' : publishNow ? 'Publish' : 'Save draft'
      }
      confirmDisabled={!valid}
      loading={save.isPending}
      onConfirm={() => save.mutate()}
    >
      <div className="space-y-3">
        <div className="space-y-1">
          <label className="text-sm font-medium">Title</label>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Module 3 is live"
          />
        </div>
        <div className="space-y-1">
          <label className="text-sm font-medium">Message</label>
          <RichTextEditor value={body} onChange={setBody} variant="basic" />
        </div>
        {!isEdit && (
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={publishNow}
              onChange={(e) => setPublishNow(e.target.checked)}
            />
            Publish now and notify enrolled students
          </label>
        )}
      </div>
    </ConfirmDialog>
  );
}
